import React, { useEffect, useMemo, useState } from 'react';
import * as Yup from "yup";
import Breadcrumbs from '../../components/BreadCrumbs';
import { getPaymentTypes, postOrder } from '../../services/orders';
import { toast } from 'react-toastify';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Form, Formik, useFormikContext } from 'formik';
import Button from '../../components/buttons/Button';
import InputField from '../../components/inputFields/InputField';
import FormikDropdown from '../../components/inputFields/FormikDropDown';
import ProductsForm from './ProductsForm';
import { searchProductWithAkaName, searchStaffUsers, searchUser } from '../../services/search';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import { callPincodeApi, applyPincodeToForm } from '../../services/pincode';
import AsyncFormikDropdown from '../../components/inputFields/AsyncFormikDropDown';
import ImagePicker from '../../components/inputFields/ImagePicker';
import ImagePickerField from '../../components/inputFields/ImagePickerField';
import UserOrderHistory from './UserOrderHistory';
import { getUserId, getUserRole } from '../../helpers/local_storage_helpers';
import { sanitizeMobile } from '../../utils/phone';

const getLocalDateString = () => {
    const now = new Date();
    const timezoneOffsetMs = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - timezoneOffsetMs).toISOString().split("T")[0];
};

const ORDER_TYPE_CHOICES = [
    { label: "Business to Consumer", value: "B2C" },
    { label: "Business to Business", value: "B2B" },
    { label: "Third Party", value: "THIRD_PARTY" },
];

const ORDER_STATUS_CHOICES = [
    { label: "Pending", value: "PENDING" },
    { label: "Confirmed", value: "CONFIRMED" },
    { label: "Packaging", value: "IN_PACKAGING" },
    { label: "Dispatched", value: "DISPATCHED" },
    { label: "Delivered", value: "DELIVERED" },
    { label: "Cancelled", value: "CANCELLED" },
];

const PLATFORM_CHOICES = [
    { label: "Telesales", value: "Telesales" },
    { label: "Toolsvilla", value: "Toolsvilla" },
    { label: "Agribegri", value: "Agribegri" },
    { label: "App", value: "App" },
    { label: "Nkosh", value: "Nkosh" },
    { label: "Behtar Zindagi", value: "Behtar Zindagi" },
];

/** Per add-order screen: only listed values are shown in each dropdown. */
const ENTRY_FIELD_RESTRICTIONS = {
    B2C: {
        order_type: ["B2C"],
        platform: ["Telesales"],
        status: ["CONFIRMED"],
    },
    B2B: {
        order_type: ["B2B"],
        platform: ["Telesales"],
    },
    THIRD_PARTY: {
        order_type: ["THIRD_PARTY"],
        platformExclude: ["Telesales"],
    },
};

const THIRD_PARTY_PLATFORM_DEFAULT =
    PLATFORM_CHOICES.find((c) => c.value !== "Telesales")?.value ?? "Toolsvilla";

const filterChoices = (choices, allowedValues) => {
    if (!allowedValues?.length) return choices;
    const allowed = new Set(allowedValues);
    return choices.filter((c) => allowed.has(c.value));
};

const getEntryChoices = (entryType, field, allChoices) => {
    const restrictions = ENTRY_FIELD_RESTRICTIONS[entryType];
    if (!restrictions) return allChoices;
    if (field === "platform" && restrictions.platformExclude?.length) {
        const excluded = new Set(restrictions.platformExclude);
        return allChoices.filter((c) => !excluded.has(c.value));
    }
    return filterChoices(allChoices, restrictions[field]);
};

const getEntryDefault = (entryType, field, fallback) => {
    const allowed = ENTRY_FIELD_RESTRICTIONS[entryType]?.[field];
    return allowed?.length === 1 ? allowed[0] : fallback;
};

const b2cAddressField = (label) =>
    Yup.string().when("order_type", {
        is: (val) => val === "B2C",
        then: (schema) => schema.trim().required(`${label} is required for B2C orders`),
        otherwise: (schema) => schema.notRequired(),
    });

const orderValidationSchema = (entryPlatformType, customerMode) => {
    const schemaFields = {
        zipcode: b2cAddressField("Zipcode"),
        line1: b2cAddressField("Line 1"),
        city: b2cAddressField("City"),
        district: b2cAddressField("District"),
        state: b2cAddressField("State"),
        third_party_order_id: Yup.string().when("order_type", {
            is: (val) => val === "THIRD_PARTY",
            then: (schema) => schema.trim().required("Third party order ID is required"),
            otherwise: (schema) => schema.notRequired(),
        }),
    };

    if (entryPlatformType === "THIRD_PARTY") {
        const allowedPlatforms = PLATFORM_CHOICES.filter((c) => c.value !== "Telesales").map((c) => c.value);
        schemaFields.platform = Yup.string().oneOf(
            allowedPlatforms,
            "Invalid platform for third party orders"
        );
        schemaFields.third_party_order_id = Yup.string()
            .trim()
            .required("Third party order ID is required");
    }

    const restrictions = ENTRY_FIELD_RESTRICTIONS[entryPlatformType];
    if (restrictions?.order_type) {
        schemaFields.order_type = Yup.string().oneOf(
            restrictions.order_type,
            "Invalid order type for this screen"
        );
    }
    if (restrictions?.platform) {
        schemaFields.platform = Yup.string().oneOf(
            restrictions.platform,
            "Invalid platform for this screen"
        );
    }
    if (restrictions?.status) {
        schemaFields.status = Yup.string().oneOf(
            restrictions.status,
            "Invalid order status for this screen"
        );
    }
    if (entryPlatformType === "B2B") {
        schemaFields.note = Yup.string()
            .trim()
            .required("Please describe how the order will go");
        if (customerMode === "existing") {
            schemaFields.name = Yup.string().trim().required("Customer name is required");
        }
    }

    if (customerMode === "walkin") {
        schemaFields.walkin_name = Yup.string().trim().required("Customer name is required");
        if (entryPlatformType !== "THIRD_PARTY") {
            schemaFields.walkin_number = Yup.string()
                .trim()
                .required("Mobile number is required")
                .test(
                    "valid-mobile",
                    "Enter a valid 10-digit mobile number",
                    (value) => sanitizeMobile(value || "").length === 10
                );
        }
    } else {
        schemaFields.user = Yup.mixed().required("Please select a customer");
    }

    return Yup.object(schemaFields);
};

function EntryFieldDefaultsSync({ entryType, statusOptions, platformOptions, orderTypeOptions }) {
    const { values, setFieldValue } = useFormikContext();

    useEffect(() => {
        const sync = (name, options) => {
            if (!options?.length) return;
            const current = values[name];
            if (!options.some((o) => o.value === current)) {
                setFieldValue(name, options[0].value);
            }
        };
        sync("status", statusOptions);
        sync("platform", platformOptions);
        sync("order_type", orderTypeOptions);
    }, [entryType, statusOptions, platformOptions, orderTypeOptions, values.status, values.platform, values.order_type, setFieldValue]);

    return null;
}

function ManageOrderForm({ platformType = "B2C" }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [rowData, setRowData] = useState(null);

    const [userOptions, setUserOptions] = useState([]);
    const [staffUsers, setStaffUsers] = useState([]);
    const [customerMode, setCustomerMode] = useState(() =>
        platformType === "THIRD_PARTY" ? "existing" : "walkin"
    ); // 'existing' | 'walkin'
    const [transportExpanded, setTransportExpanded] = useState(false);
    const paymentTypeDropdown = [
        {
            label: "UPI",
            value: "UPI",
        },
        {
            label: "CARD",
            value: "CARD",
        },
        {
            label: "CASH",
            value: "CASH",
        },
        {
            label: "CHEQUE",
            value: "CHEQUE",
        },
        {
            label: "BANK_TRANSFER",
            value: "BANK_TRANSFER",
        },
        {
            label: "RAZORPAY",
            value: "Razorpay",
        },
        {
            label: "SBI_UPI",
            value: "SBI_UPI",
        },
        {
            label: "SARAL_UPI",
            value: "SARAL_UPI",
        },
    ];
    const [paymentScreenShot, setPaymentScreenShot] = useState(null);
    const [transportImage, setTransportImage] = useState(null);
    const [selectedUser, setSelectedUser] = useState("");
    const [isPaymentDateManuallyChanged, setIsPaymentDateManuallyChanged] = useState(false);

    useEffect(() => {
        if (location.state) {
            setRowData(location.state);
        }
    }, [location]);

    useEffect(() => {
        if (getUserRole() === 'admin') {
            searchStaffUsers({ query: '' })
                .then(res => {
                    const users = Array.isArray(res.data) ? res.data : (res.data?.results || []);
                    setStaffUsers([
                        { value: "", label: "— None —" },
                        ...users.map(u => ({
                            value: u.id,
                            label: u.name || u.username || `Staff #${u.id}`,
                        })),
                    ]);
                })
                .catch(() => {});
        }
    }, []);

    const entryStatusOptions = useMemo(
        () => getEntryChoices(platformType, "status", ORDER_STATUS_CHOICES),
        [platformType]
    );
    const entryPlatformOptions = useMemo(
        () => getEntryChoices(platformType, "platform", PLATFORM_CHOICES),
        [platformType]
    );
    const entryOrderTypeOptions = useMemo(
        () => getEntryChoices(platformType, "order_type", ORDER_TYPE_CHOICES),
        [platformType]
    );

    const orderFormInitialValues = useMemo(
        () => ({
            full_name: rowData?.user?.name || "",
            name: rowData?.user?.name || "",
            phone_number: rowData?.user?.phone_number || "",
            amount: rowData?.total_price || "",
            status: rowData?.status || getEntryDefault(platformType, "status", "CONFIRMED"),
            payment_status: rowData?.payments?.[0]?.payment_status || "COMPLETED",
            payment_mode: rowData?.payments?.[0]?.payment_mode || "SBI_UPI",
            payment_date: rowData?.payments?.[0]?.payment_date || getLocalDateString(),
            platform:
                platformType === "THIRD_PARTY"
                    ? (rowData?.platform && rowData.platform !== "Telesales"
                        ? rowData.platform
                        : THIRD_PARTY_PLATFORM_DEFAULT)
                    : (rowData?.platform || getEntryDefault(platformType, "platform", "Telesales")),
            order_type: rowData?.order_type || getEntryDefault(platformType, "order_type", platformType || "B2C"),
            payment_id: "",
            line1: "",
            line2: "",
            landmark: "",
            city: "",
            district: "",
            state: "",
            payment_amount: "",
            country: "",
            total_items: "",
            alternate_phone: "",
            zipcode: "",
            note: "",
            gstin: "",
            transport_name: "",
            transport_number: "",
            delivery_charge: 0,
            discount: 0,
            walkin_name: "",
            walkin_number: "",
            shop_name: "",
            third_party_order_id: rowData?.third_party_order_id || "",
            customer_mobile: "",
            items: [],
            order_date: getLocalDateString(),
            user: "",
            sales_person: platformType === "THIRD_PARTY" ? (rowData?.sales_person || "") : getUserId(),
        }),
        [rowData, platformType]
    );

    const validationSchema = useMemo(
        () => orderValidationSchema(platformType, customerMode),
        [platformType, customerMode]
    );

    const handlePaymentScreenshotSelect = (file) => {
        setPaymentScreenShot(file);
    };

    const handleTransportImageSelect = (file) => {
        setTransportImage(file);
    };

    const handleDiscardChanges = (resetForm) => {
        resetForm();
        navigate(-1);
    };

    const handleFormSubmit = async (values, { setSubmitting }) => {
        try {
            const formData = new FormData();

            // 1. Append files first (matches cURL)
            if (paymentScreenShot) {
                formData.append("payment_proof", paymentScreenShot);
            }
            if (transportImage) {
                formData.append("transport_image", transportImage);
            }

            // 2. Append simple fields (exact names from cURL)
            const resolvedOrderType = values.order_type || platformType || "B2C";
            if (customerMode === 'existing') {
                formData.append("user_id", values.user);
                if (resolvedOrderType === "B2B" && values.name?.trim()) {
                    formData.append("name", values.name.trim());
                }
            } else {
                formData.append("name", values.walkin_name);
                formData.append("number", values.walkin_number);
            }
            formData.append("total_price", values.amount);
            formData.append("total_items", values.total_items);
            formData.append("status", values.status);
            formData.append("platform", values.platform);
            formData.append("delivery_charge", values.delivery_charge || "0");
            formData.append("discount", values.discount || "0");
            formData.append("customer_gstin", values.gstin || "");
            formData.append("order_type", resolvedOrderType);
            formData.append("shop_name", values.shop_name || "");
            if (resolvedOrderType === "THIRD_PARTY") {
                formData.append("third_party_order_id", values.third_party_order_id || "");
            }
            if (resolvedOrderType !== "THIRD_PARTY") {
                if (getUserRole() === "admin") {
                    if (values.sales_person) formData.append("sales_person", values.sales_person);
                } else {
                    formData.append("sales_person", getUserId());
                }
            } else if (getUserRole() === "admin" && values.sales_person) {
                formData.append("sales_person", values.sales_person);
            }
            if (getUserRole() === "admin") {
                formData.append("order_date", values.order_date);
            }

            // 3. Shipping address - WRAP IN ARRAY like cURL does
            const shippingAddress = [{
                line1: values.line1,
                line2: values.line2 || "",
                landmark: values.landmark || "",
                city: values.city,
                district: values.district,
                state: values.state,
                country: values.country,
                alternate_phone: values.alternate_phone || "",
                zipcode: values.zipcode,
                note: values.note || "",
                shop_name: values.shop_name || "",
                transport_name: values.transport_name || "",
                transport_number: values.transport_number || "",
                transport_image_field_name: "transport_image",
            }];
            formData.append("shipping_address", JSON.stringify(shippingAddress));

            // 4. Items - WRAP IN ARRAY and match field names
            const items = values.items.map(item => {
                const mappedItem = {
                    shop_product_id: item.shop_product_id,
                    quantity: item.quantity,
                    price: item.price.toString(), // String like in cURL
                    total_price: item.total_price
                };
                if (resolvedOrderType === "B2B") {
                    mappedItem.is_taxed_price = item.is_taxed_price === true;
                }
                mappedItem.dispatch_note = item.dispatch_note || null;
                return mappedItem;
            });
            formData.append("items", JSON.stringify(items));

            // 5. Payments - WRAP IN ARRAY and match structure
            const payments = [{
                payment_mode: values.payment_mode,
                payment_amount: values.payment_amount,
                payment_status: values.payment_status,
                payment_id: values.payment_id || "",
                image_field_name: "payment_proof",
                ...(getUserRole() === "admin" && { payment_date: values.payment_date }),
            }];
            formData.append("payments", JSON.stringify(payments));

            const response = await postOrder(formData);

            if (response?.status === 201) {
                if (response?.data?.stock_errors) {
                    for (const error of response?.data?.stock_errors) {
                        toast.error(error);
                    }
                }
                toast.success("Order added successfully!");
                navigate(-1);
                return;
            } else {
                toast.error(response?.data?.message || "Order submission failed");
            }

        } catch (error) {
            console.error("Submission error:", error.response?.data || error);
            toast.error(error.response?.data?.error || "Failed to submit order");
        } finally {
            setSubmitting(false);
        }
    };

    return (<>
        <Breadcrumbs items={[
            { name: 'Home', link: '/dashboard' },
            { name: 'Manage Orders', link: (-1) },
            { name: rowData ? 'Update Order' : 'Generate Order' },
        ]} />

        <div className="mt-4 mb-10">
            <Formik
                enableReinitialize
                initialValues={orderFormInitialValues}
                validationSchema={validationSchema}
                onSubmit={handleFormSubmit}
            >
                {({ isSubmitting, setFieldValue, resetForm, values }) => {
                    const isB2COrder = values.order_type === "B2C";
                    const isB2BOrder = values.order_type === "B2B";
                    const isThirdPartyOrder = values.order_type === "THIRD_PARTY";
                    const isThirdParty = platformType === "THIRD_PARTY" || isThirdPartyOrder;
                    const isB2BEntry = platformType === "B2B";

                    const transportDetailsBlock = (embedded = false) => (
                        <div className={embedded ? "border-t border-gray-100 pt-3" : "bg-white rounded-xl shadow-sm border border-gray-200 mb-3"}>
                            <button
                                type="button"
                                onClick={() => setTransportExpanded(!transportExpanded)}
                                className={`w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 transition-colors ${embedded ? "" : "rounded-xl"}`}
                            >
                                <span className="text-base font-semibold text-gray-700">🚚 Transport Details</span>
                                <span className={`text-gray-400 transition-transform duration-200 ${transportExpanded ? "rotate-180" : ""}`}>
                                    ▼
                                </span>
                            </button>
                            {transportExpanded && (
                                <div className={`${embedded ? "pt-3" : "p-5 border-t border-gray-100"} flex flex-col gap-4`}>
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                                        <InputField label="Transport Name" name="transport_name" type="text" placeholder="e.g. DTDC, BlueDart" />
                                        <InputField label="Transport Number" name="transport_number" type="text" placeholder="Tracking / Docket No." />
                                    </div>
                                    <div>
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Transport Image</p>
                                        <ImagePickerField
                                            label=""
                                            preloadedImage={transportImage}
                                            onImageSelect={handleTransportImageSelect}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    );

                    return (
                    <Form onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}>
                        <EntryFieldDefaultsSync
                            entryType={platformType}
                            statusOptions={entryStatusOptions}
                            platformOptions={entryPlatformOptions}
                            orderTypeOptions={entryOrderTypeOptions}
                        />

                        {/* ── SECTION 1: Order Info ───────────────────────────────────── */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-3">
                            <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-gray-100">
                                <span className="text-base font-semibold text-gray-700">🧾 Order Information</span>
                                <div className="flex rounded-lg overflow-hidden border border-gray-300 text-xs font-medium">
                                    <button
                                        type="button"
                                        onClick={() => setCustomerMode('existing')}
                                        className={`px-3 py-1.5 transition-colors whitespace-nowrap ${customerMode === 'existing' ? 'bg-primary text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                    >
                                        👤 Existing
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setCustomerMode('walkin')}
                                        className={`px-3 py-1.5 transition-colors whitespace-nowrap ${customerMode === 'walkin' ? 'bg-primary text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                    >
                                        🚶 Walk-in
                                    </button>
                                </div>
                            </div>
                            <div className="p-4 flex flex-col gap-3">

                                {/* ── Row 1: Customer Name/Mobile/Alternate/Order Date ── */}
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                                    {platformType === "THIRD_PARTY" || isThirdPartyOrder ? (
                                        <InputField
                                            label="Third Party Order ID"
                                            name="third_party_order_id"
                                            type="text"
                                            placeholder="TV-98765"
                                            required
                                        />
                                    ) : null}

                                    {customerMode === 'existing' ? (
                                        <div className="flex gap-2 items-end">
                                            <div className="flex-1">
                                                <AsyncFormikDropdown
                                                    label="Customer Name"
                                                    name="user"
                                                    placeholder="Search by name, mobile or shop name..."
                                                    apiCallFunction={async (input) => {
                                                        const response = await searchUser(input);
                                                        setUserOptions(response.data);
                                                        return response.data.map((p) => ({
                                                            value: p.id,
                                                            label: `${p.shop_name || ""} (${p.name}) | ${p.phone_number} ${p.address ? "| " + p.address.state : ""}`,
                                                        }));
                                                    }}
                                                    required
                                                    needSearch={true}
                                                    onChange={(selectedOption) => {
                                                        const selectedPatient = userOptions.find((item) => item.id === selectedOption.value);
                                                        if (selectedPatient) {
                                                            setFieldValue("name", selectedPatient.name);
                                                            setFieldValue("shop_name", selectedPatient.shop_name || "");
                                                            setFieldValue("customer_mobile", selectedPatient.phone_number || "");
                                                            setFieldValue("alternate_phone", selectedPatient.phone_number || "");
                                                            setSelectedUser(selectedPatient.id);
                                                            if (selectedPatient.address) {
                                                                setFieldValue("line1", selectedPatient.address.line1 || "");
                                                                setFieldValue("line2", selectedPatient.address.line2 || "");
                                                                setFieldValue("landmark", selectedPatient.address.landmark || "");
                                                                setFieldValue("city", selectedPatient.address.city || "");
                                                                setFieldValue("district", selectedPatient.address.district || "");
                                                                setFieldValue("state", selectedPatient.address.state || "");
                                                                setFieldValue("country", selectedPatient.address.country || "");
                                                                setFieldValue("zipcode", selectedPatient.address.zipcode || "");
                                                                setFieldValue("gstin", selectedPatient.gstin || "");
                                                            } else {
                                                                ["line1", "line2", "landmark", "city", "district", "state", "country", "zipcode", "gstin"].forEach(f => setFieldValue(f, ""));
                                                            }
                                                        }
                                                    }}
                                                />
                                            </div>
                                            <Link to="/farmers/add" title="Add new customer">
                                                <div className="bg-primary text-white h-10 w-10 flex items-center justify-center rounded-lg text-xl font-bold hover:opacity-80 transition">+</div>
                                            </Link>
                                        </div>
                                    ) : !isB2BOrder ? (
                                        <InputField label="Customer Name" name="walkin_name" type="text" placeholder="Enter full name" required />
                                    ) : null}

                                    {isB2BOrder ? (
                                        <InputField
                                            label="Shop Name"
                                            name="shop_name"
                                            type="text"
                                            placeholder="Enter shop / business name"
                                        />
                                    ) : null}

                                    {isB2BOrder && customerMode === "existing" ? (
                                        <InputField
                                            label="Customer Name"
                                            name="name"
                                            type="text"
                                            placeholder="Enter or edit customer name"
                                            required
                                        />
                                    ) : customerMode === "walkin" ? (
                                        <InputField label="Customer Name" name="walkin_name" type="text" placeholder="Enter full name" required />
                                    ) : null}

                                    {!isThirdParty ? (
                                        <>
                                            <InputField
                                                label="Mobile Number"
                                                name={customerMode === 'existing' ? 'customer_mobile' : 'walkin_number'}
                                                type="text"
                                                placeholder="10-digit mobile number"
                                                required={customerMode !== 'existing'}
                                                disabled={customerMode === 'existing'}
                                                onChange={(e) => {
                                                    const sanitized = sanitizeMobile(e.target.value);
                                                    setFieldValue(customerMode === 'existing' ? 'customer_mobile' : 'walkin_number', sanitized);
                                                }}
                                            />
                                            <InputField
                                                label="Alternate Number"
                                                name="alternate_phone"
                                                type="text"
                                                placeholder="Alternate number"
                                                onChange={(e) => setFieldValue('alternate_phone', sanitizeMobile(e.target.value))}
                                            />
                                        </>
                                    ) : null}

                                    {getUserRole() === "admin" && (
                                        <InputField
                                            label="Order Date"
                                            name="order_date"
                                            type="date"
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                setFieldValue("order_date", val);
                                                if (!isPaymentDateManuallyChanged) {
                                                    setFieldValue("payment_date", val);
                                                }
                                            }}
                                        />
                                    )}
                                </div>

                                {/* ── Row 2: Remaining fields (5) ── */}
                                <div className="border-t border-gray-100 pt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                                    <InputField label="GSTIN" name="gstin" placeholder="XXXXXXXXXXX" />
                                    <FormikDropdown
                                        label="Order Status"
                                        name="status"
                                        placeholder="Select Status"
                                        required={true}
                                        options={entryStatusOptions}
                                    />
                                    <FormikDropdown
                                        label="Platform"
                                        name="platform"
                                        placeholder="Select Platform"
                                        required={true}
                                        options={entryPlatformOptions}
                                    />
                                    <FormikDropdown
                                        label="Order Type"
                                        name="order_type"
                                        placeholder="Select order type"
                                        required={true}
                                        options={entryOrderTypeOptions}
                                    />
                                    {getUserRole() === "admin" && platformType !== "THIRD_PARTY" && (
                                        <FormikDropdown
                                            label="Sales Person"
                                            name="sales_person"
                                            placeholder="Select Sales Person"
                                            options={staffUsers}
                                        />
                                    )}
                                </div>

                                {/* ── Row 3: Address / Note ── */}
                                <div className="border-t border-gray-100 pt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {isThirdParty ? (
                                        <InputField
                                            label="Delivery Note"
                                            name="note"
                                            type="text"
                                            placeholder="Delivery note (Optional)"
                                        />
                                    ) : (
                                        <>
                                            <InputField
                                                label="Zipcode"
                                                name="zipcode"
                                                type="text"
                                                placeholder="452001"
                                                required={isB2COrder}
                                                onChange={async (value) => {
                                                    setFieldValue("zipcode", value.target.value);
                                                    if (value.target.value.length === 6) {
                                                        const resp = await callPincodeApi({ value: value.target.value });
                                                        applyPincodeToForm(setFieldValue, resp);
                                                    }
                                                }}
                                            />
                                            <InputField label="Line 1" name="line1" type="text" placeholder="Street / Colony" required={isB2COrder} />
                                            <InputField label="Landmark" name="landmark" type="text" placeholder="Landmark (Optional)" />
                                            <InputField label="City" name="city" type="text" placeholder="City" required={isB2COrder} />
                                            <InputField label="District" name="district" type="text" placeholder="District" required={isB2COrder} />
                                            <InputField label="State" name="state" type="text" placeholder="State" required={isB2COrder} />
                                            <InputField
                                                label="Delivery Note"
                                                name="note"
                                                type="text"
                                                placeholder={isB2BEntry ? "How will the order go?" : "Delivery note (Optional)"}
                                                required={isB2BEntry}
                                            />
                                        </>
                                    )}
                                </div>

                                {isB2BOrder ? transportDetailsBlock(true) : null}

                            </div>
                        </div>


                        {/* ── SECTION 2: Products ─────────────────────────────────────── */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-3">
                            <div className="p-3">
                                <ProductsForm searchProductApi={searchProductWithAkaName} compactMode={true} />
                                {/* Hidden computed fields */}
                                <div className="hidden">
                                    <InputField name="delivery_charge" label="" />
                                    <InputField name="total_items" label="" />
                                    <InputField name="amount" label="" />
                                </div>
                            </div>
                        </div>

                        {/* ── SECTION 3: Payment ────────────────────────────── */}
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-3">
                            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-gray-100">
                                <span className="text-base font-semibold text-gray-700">💳 Payment</span>
                            </div>
                            <div className="p-4 flex flex-col gap-3">
                                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                                    <InputField label="Payment Amount" name="payment_amount" placeholder="₹ Amount" />
                                    <FormikDropdown
                                        label="Payment Mode"
                                        name="payment_mode"
                                        placeholder="Select Mode"
                                        required={true}
                                        options={paymentTypeDropdown}
                                    />
                                    {getUserRole() === "admin" && (
                                        <InputField
                                            label="Payment Date"
                                            name="payment_date"
                                            type="date"
                                            placeholder="Payment Date"
                                            onChange={(e) => {
                                                setIsPaymentDateManuallyChanged(true);
                                                setFieldValue("payment_date", e.target.value);
                                            }}
                                        />
                                    )}
                                    <FormikDropdown
                                        label="Payment Status"
                                        name="payment_status"
                                        placeholder="Select Status"
                                        required={true}
                                        options={[
                                            { label: "Pending", value: "PENDING" },
                                            { label: "Completed", value: "COMPLETED" },
                                        ]}
                                    />
                                    <InputField label="Transaction Number" name="payment_id" placeholder="Enter Txn. ID" />
                                </div>
                                <div className="h-36">
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Payment Proof</p>
                                    <ImagePicker
                                        preloadedImage={paymentScreenShot}
                                        placeholder='Select Payment Proof'
                                        onImageSelect={handlePaymentScreenshotSelect}
                                    />
                                </div>
                            </div>
                        </div>

                        {!isB2BOrder ? transportDetailsBlock(false) : null}

                        {/* ── Action Bar ──────────────────────────────────────────────── */}
                        <div className="flex justify-end gap-3 pb-6">
                            <Button
                                type="button"
                                className="px-8 py-2 rounded-lg border border-primary text-primary bg-white hover:bg-gray-50 font-medium"
                                onClick={() => handleDiscardChanges(resetForm)}
                                disabled={isSubmitting}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                className="px-10 py-2 rounded-lg bg-primary text-white hover:opacity-90 font-medium"
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? "Saving..." : "Save Order"}
                            </Button>
                        </div>

                    </Form>
                    );
                }}
            </Formik>
        </div>


        {selectedUser && (
            <div className="flex flex-col pt-4 gap-3">
                <StyledHeadingText text={"User Order History"} />
                <UserOrderHistory user_id={selectedUser} showTopItems={false} />
            </div>
        )}
    </>);
}

export default ManageOrderForm;
